/**
 * V114-5-1 — React data hooks for the gap-fulfillment rows and the webhook admin page. Gap keys live
 * under the monitoring root (['monitoring', slug, …]) so a decline refreshes coverage, candidates and
 * the gap list in one invalidation; webhook keys are their own root (nothing else reads them).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from './useWorkspaceQuery';
import { monitoringKeys } from './useMonitoring';
import { monitoringErrorOf } from '../lib/monitoring-api';
import {
  createOutboundWebhook, declineGap, deleteOutboundWebhook, getGapBrief, listGaps,
  listOutboundWebhookDeliveries, listOutboundWebhooks, redeliverOutboundWebhook,
  reopenControlNotApplicable, rotateOutboundWebhookSecret, sendOutboundWebhookTest,
} from '../lib/gaps-api';
import { getMyHats } from '../lib/setup-api';

/** Don't retry what retrying cannot fix. */
const retry = (count: number, err: Error) => {
  const k = monitoringErrorOf(err).kind;
  return !(k === 'not_found' || k === 'forbidden' || k === 'validation') && count < 1;
};

export const gapKeys = {
  list: (slug: string, systemId: string | null) => ['monitoring', slug, 'gaps', systemId ?? 'all'] as const,
  brief: (slug: string, gapKey: string) => ['monitoring', slug, 'gap-brief', gapKey] as const,
};
export const webhookKeys = {
  root: (slug: string) => ['outbound-webhooks', slug] as const,
  list: (slug: string) => ['outbound-webhooks', slug, 'list'] as const,
  deliveries: (slug: string, endpointId: string) => ['outbound-webhooks', slug, 'deliveries', endpointId] as const,
};

/** The open gaps (one system's, or the workspace's with null). Any member. */
export const useGaps = (systemId: string | null) =>
  useWorkspaceQuery((slug) => gapKeys.list(slug, systemId), (slug) => listGaps(slug, systemId), { retry });

/** One gap's rendered brief; fetched lazily (pass enabled when the disclosure opens). */
export const useGapBrief = (gapKey: string | null, enabled: boolean) =>
  useWorkspaceQuery(
    (slug) => gapKeys.brief(slug, gapKey ?? ''),
    (slug) => getGapBrief(slug, gapKey!),
    { enabled: enabled && !!gapKey, retry });

/** Decline a gap (the governing record changes, D-114-10) and refresh every monitoring read. */
export function useDeclineGap() {
  const { workspace } = useWorkspace();
  const slug = workspace?.slug ?? '';
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (input: { gapKey: string; reason: string }) => declineGap(slug, input.gapKey, input.reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: monitoringKeys.root(slug) }),
  });
  return { ...m, failure: m.error ? monitoringErrorOf(m.error) : null };
}

/** Reopen an N/A'd control (panel B1; owner / admin) and refresh every monitoring read. */
export function useReopenControl() {
  const { workspace } = useWorkspace();
  const slug = workspace?.slug ?? '';
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (input: { systemId: string; controlId: string }) => reopenControlNotApplicable(slug, input.systemId, input.controlId),
    onSuccess: () => qc.invalidateQueries({ queryKey: monitoringKeys.root(slug) }),
  });
  return { ...m, failure: m.error ? monitoringErrorOf(m.error) : null };
}

/** The enabler decline gate's second door (program lead) — the same read Setup uses. */
export function useIsProgramLead(): boolean {
  const { workspace } = useWorkspace();
  const slug = workspace?.slug ?? '';
  const q = useQuery({ queryKey: ['assignments-me', slug], queryFn: () => getMyHats(slug), retry: false, staleTime: 60_000, enabled: !!slug });
  return (q.data?.responsibilities ?? []).includes('program_lead');
}

// ---------------------------------------------------------------------------
// Outbound webhooks (admin only; pass enabled=false for other roles so the page never asks for a 403)
// ---------------------------------------------------------------------------

export const useOutboundWebhooks = (enabled = true) =>
  useWorkspaceQuery(webhookKeys.list, (slug) => listOutboundWebhooks(slug), { enabled, retry });

export const useWebhookDeliveries = (endpointId: string | null) =>
  useWorkspaceQuery(
    (slug) => webhookKeys.deliveries(slug, endpointId ?? ''),
    (slug) => listOutboundWebhookDeliveries(slug, endpointId!),
    { enabled: !!endpointId, retry });

function useWebhookMutation<TInput, TOutput>(fn: (slug: string, input: TInput) => Promise<TOutput>) {
  const { workspace } = useWorkspace();
  const slug = workspace?.slug ?? '';
  const qc = useQueryClient();
  const m = useMutation<TOutput, Error, TInput>({
    mutationFn: (input) => fn(slug, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: webhookKeys.root(slug) }),
  });
  return { ...m, failure: m.error ? monitoringErrorOf(m.error) : null };
}

export const useCreateWebhook = () =>
  useWebhookMutation((slug, input: { url: string; label?: string }) => createOutboundWebhook(slug, input));
export const useDeleteWebhook = () =>
  useWebhookMutation((slug, endpointId: string) => deleteOutboundWebhook(slug, endpointId));
export const useRotateWebhookSecret = () =>
  useWebhookMutation((slug, endpointId: string) => rotateOutboundWebhookSecret(slug, endpointId));
export const useSendWebhookTest = () =>
  useWebhookMutation((slug, endpointId: string) => sendOutboundWebhookTest(slug, endpointId));
export const useRedeliverWebhook = () =>
  useWebhookMutation((slug, input: { endpointId: string; deliveryId: string }) => redeliverOutboundWebhook(slug, input.endpointId, input.deliveryId));
